import { SELECTORS, CLASS_NAMES } from '@bs/constants';

/**
 * Number of repeater blocks revealed per "page" below the desktop breakpoint.
 * @constant {number}
 */
const ITEMS_PER_PAGE_MOBILE = 4;

/**
 * Number of repeater blocks revealed per "page" at the desktop breakpoint
 * (1440px) and above.
 * @constant {number}
 */
const ITEMS_PER_PAGE_DESKTOP = 9;

/**
 * Media query that marks the switch to the 9-per-page desktop layout.
 * @constant {string}
 */
const DESKTOP_MEDIA_QUERY = '(min-width: 1440px)';

/**
 * A class for the Financials module. Progressively reveals the repeater blocks:
 * a "Show more" button exposes the next batch of items each time it is clicked.
 * The batch size is responsive — {@link ITEMS_PER_PAGE_MOBILE} below the desktop
 * breakpoint and {@link ITEMS_PER_PAGE_DESKTOP} at 1440px and above — and the
 * button hides itself once every block is visible.
 */
export default class Financials {
  /**
   * Constructor function of the Financials class. Must receive one argument, a
   * DOM node representing the module's container.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    /**
     * DOM node that is passed into the constructor
     * @property {HTMLElement} element DOM node that is passed into the constructor
     */
    this.element = element;

    /**
     * Media query list used to detect the desktop layout
     * @property {MediaQueryList} desktopMq The (min-width: 1440px) query
     */
    this.desktopMq = window.matchMedia(DESKTOP_MEDIA_QUERY);

    /**
     * How many blocks each "Show more" click reveals at the current breakpoint
     * @property {number} step The active per-page batch size
     */
    this.step = 0;

    /**
     * How many blocks are currently visible
     * @property {number} visibleCount The count of revealed blocks
     */
    this.visibleCount = 0;

    this.init();
  }

  /**
   * Initializes the view by calling the functions to create DOM references,
   * setup event handlers and then bind them to the view.
   * @returns {Financials} A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  /**
   * Find all necessary DOM elements used in the view and cache them.
   * @returns {Financials} A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.repeater = this.element.querySelector(SELECTORS.FINANCIALS_REPEATER);
    this.blocks = Array.from(
      this.element.querySelectorAll(SELECTORS.FINANCIALS_BLOCK)
    );
    this.showMore = this.element.querySelector(SELECTORS.FINANCIALS_SHOW_MORE);

    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {Financials} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.handleShowMore = this.onShowMore.bind(this);
    this.handleBreakpointChange = this.onBreakpointChange.bind(this);

    return this;
  }

  /**
   * Attach the event listeners to enable interaction with the view, set the
   * initial visible count and render the first page.
   * @returns {Financials} A reference to the current instance of the class
   * @chainable
   */
  enable() {
    if (!this.showMore || !this.blocks.length) {
      return this;
    }

    this.step = this.getStep();
    this.visibleCount = Math.min(this.step, this.blocks.length);

    this.showMore.addEventListener('click', this.handleShowMore);
    this.desktopMq.addEventListener('change', this.handleBreakpointChange);

    this.update();

    return this;
  }

  /**
   * Tears the module down, removing every listener it attached.
   * @returns {Financials} A reference to the current instance of the class
   * @chainable
   */
  destroy() {
    if (this.showMore) {
      this.showMore.removeEventListener('click', this.handleShowMore);
    }

    this.desktopMq.removeEventListener('change', this.handleBreakpointChange);

    return this;
  }

  /**
   * Returns the per-page batch size for the active breakpoint.
   * @returns {number} The number of blocks to reveal per "Show more" click
   */
  getStep() {
    return this.desktopMq.matches
      ? ITEMS_PER_PAGE_DESKTOP
      : ITEMS_PER_PAGE_MOBILE;
  }

  /**
   * Reveals the next batch of blocks, then moves focus to the first newly
   * revealed block so keyboard and screen-reader users land on the new content.
   * @returns {Financials} A reference to the current instance of the class
   */
  onShowMore() {
    const firstNewIndex = this.visibleCount;

    this.step = this.getStep();
    this.visibleCount = Math.min(
      this.visibleCount + this.step,
      this.blocks.length
    );

    this.update().focusBlock(firstNewIndex);

    return this;
  }

  /**
   * Recomputes the batch size when the desktop breakpoint is crossed. Already
   * visible blocks are never hidden, and at least one full page is always shown.
   * @returns {Financials} A reference to the current instance of the class
   */
  onBreakpointChange() {
    this.step = this.getStep();
    this.visibleCount = Math.min(
      Math.max(this.visibleCount, this.step),
      this.blocks.length
    );

    this.update();

    return this;
  }

  /**
   * Toggles the hidden state on every block based on the current visible count
   * and hides the "Show more" button once all blocks are visible.
   * @returns {Financials} A reference to the current instance of the class
   */
  update() {
    this.blocks.forEach((block, index) => {
      block.classList.toggle(
        CLASS_NAMES.FINANCIALS_BLOCK_HIDDEN,
        index >= this.visibleCount
      );
    });

    const allVisible = this.visibleCount >= this.blocks.length;
    this.showMore.classList.toggle(
      CLASS_NAMES.FINANCIALS_SHOW_MORE_HIDDEN,
      allVisible
    );

    return this;
  }

  /**
   * Moves focus to a block by index, making it programmatically focusable if it
   * is not already.
   * @param {number} index The index of the block to focus
   * @returns {Financials} A reference to the current instance of the class
   */
  focusBlock(index) {
    const block = this.blocks[index];
    if (!block) return this;

    if (!block.hasAttribute('tabindex')) {
      block.setAttribute('tabindex', '-1');
    }
    block.focus();

    return this;
  }
}
